import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCoach: boolean;
  isPlayerParent: boolean;
  isPending: boolean;
  isRejected: boolean;
  linkedPlayerId: string | null;
  requestedRole: string | null;
  teamIds: string[];
  isTeamManager: boolean;
  isPendingTeamManager: boolean;
  managedTeamIds: string[];
  isLeagueManager: boolean;
  managedLeagueIds: string[];
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
  }, []);

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, token, login, logout,
      isAuthenticated:      !!token,
      isAdmin:              user?.role === 'admin',
      isCoach:              user?.role === 'coach',
      isPlayerParent:       user?.role === 'player_parent',
      isPending:            user?.account_status === 'pending',
      isRejected:           user?.account_status === 'rejected',
      linkedPlayerId:       user?.linked_player_id ?? null,
      requestedRole:        user?.requested_role ?? null,
      teamIds:              user?.team_ids ?? [],
      isTeamManager:        user?.team_manager_status === 'active',
      isPendingTeamManager: user?.team_manager_status === 'pending',
      managedTeamIds:       user?.managed_team_ids ?? [],
      isLeagueManager:      user?.role === 'league_manager' || (Array.isArray(user?.managed_league_ids) && (user?.managed_league_ids?.length ?? 0) > 0),
      managedLeagueIds:     user?.managed_league_ids ?? [],
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
